import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { EmptyState, Money, PageHeader, StatusBadge, TableSkeleton, fmtDate } from "../components/erp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";

export default function Invoices() {
  const { activeId } = useCompany();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const nav = useNavigate();

  const load = () => {
    const qs = new URLSearchParams();
    if (search) qs.set("search", search);
    if (status !== "all") qs.set("status", status);
    api.get(`/invoices?${qs}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [activeId, status]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [search]); // eslint-disable-line

  return (
    <div data-testid="invoices-page">
      <PageHeader title="Sales Invoices" testId="invoices-title" sub="GST billing · material, labour & composite">
        <Button data-testid="new-invoice-btn" onClick={() => nav("/invoices/new")}
          className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><Plus className="mr-1.5 h-4 w-4" /> New Invoice</Button>
      </PageHeader>

      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="invoices-search" className="pl-9 bg-white dark:bg-slate-900" placeholder="Search invoice number..."
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="invoices-status-filter" className="w-44 bg-white dark:bg-slate-900"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="posted">Posted</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        {!data ? <TableSkeleton /> : data.items.length === 0 ? (
          <EmptyState testId="invoices-empty" title="No invoices found"
            body="Create your first GST invoice — stock, ledger and receivables update automatically."
            actionLabel="New Invoice" actionTo="/invoices/new" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="invoices-table">
              <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="p-3">Invoice No</th><th className="p-3">Date</th><th className="p-3">Customer</th>
                <th className="p-3">Type</th><th className="p-3 text-right">Total</th><th className="p-3 text-right">Paid</th>
                <th className="p-3 text-right">Balance</th><th className="p-3">Status</th></tr></thead>
              <tbody>
                {data.items.map((inv) => (
                  <tr key={inv.id} data-testid={`invoice-row-${inv.invoice_no}`} onClick={() => nav(`/invoices/${inv.id}`)}
                    className="cursor-pointer border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors">
                    <td className="p-3 font-mono text-xs font-semibold">
                      {inv.invoice_no} {inv.is_demo && <Badge variant="secondary" className="ml-1 text-[10px]">DEMO</Badge>}
                    </td>
                    <td className="p-3">{fmtDate(inv.date)}</td>
                    <td className="p-3 font-medium max-w-[200px] truncate">{inv.party_name}</td>
                    <td className="p-3 capitalize text-slate-500">{inv.invoice_type}</td>
                    <td className="p-3 text-right"><Money value={inv.total} className="font-semibold" /></td>
                    <td className="p-3 text-right"><Money value={inv.paid} className="text-emerald-600 dark:text-emerald-400" /></td>
                    <td className="p-3 text-right"><Money value={inv.balance} className={inv.balance > 0 ? "text-red-600 dark:text-red-400" : "text-slate-400"} /></td>
                    <td className="p-3"><StatusBadge status={inv.payment_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="mt-2 text-xs text-slate-400">{data?.total ?? 0} invoices</p>
    </div>
  );
}
